import React, { useState, useContext } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import AuthContext from "../../contexts/AuthContext/AuthContext";

// Function to decode JWT
const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return {};
  }
};

const SignInPage = () => {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isForgotUsername, setIsForgotUsername] = useState(false);
  const [emailForReset, setEmailForReset] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [isRegisterPrompt, setIsRegisterPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const response = await axios.post(`${SERVER_URL}/api/auth/jwt/create/`, {
        username: userName,
        password: password,
      });
      const { access, refresh } = response.data;
      const decodedToken = decodeJWT(access);
      const userData = {
        username: userName,
        id: decodedToken.user_id || decodedToken.sub || null,
      };
      login(userData, access, refresh);
      navigate("/");
    } catch (err) {
      setError(
        err.response?.data?.detail || "Invalid credentials. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage("");
    setError("");
    setIsLoading(true);
    try {
      await axios.post(`${SERVER_URL}/auth/users/reset_password/`, {
        email: emailForReset,
      });
      setResetMessage("Password reset email sent. Check your inbox.");
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Email not registered.");
        setIsRegisterPrompt(true);
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotUsername = async (e) => {
    e.preventDefault();
    setResetMessage("");
    setError("");
    setIsLoading(true);
    try {
      await axios.post(`${SERVER_URL}/api/forgot_username/`, {
        email: emailForReset,
      });
      setResetMessage("Username reminder email sent. Check your inbox.");
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Email not registered.");
        setIsRegisterPrompt(true);
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 sm:px-0">
      <Helmet>
        <title>Sign In – LingoLab</title>
        <meta
          name="description"
          content="Sign in to your LingoLab account to access AI-powered video translation and localization services."
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://lingolab.ca/signin" />
        <meta property="og:title" content="Sign In – LingoLab" />
        <meta
          property="og:description"
          content="Sign in to your LingoLab account to access AI-powered video translation and localization services."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lingolab.ca/signin" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LingLabAI" />
        <meta name="twitter:title" content="Sign In – LingoLab" />
        <meta
          name="twitter:description"
          content="Sign in to your LingoLab account to access AI-powered video translation and localization services."
        />
        <meta name="twitter:image" content="https://lingolab.ca/ll-X.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "LingoLab",
              "url": "https://lingolab.ca/",
              "logo": "https://lingolab.ca/ll-X.png",
              "sameAs": [
                "https://twitter.com/LingLabAI"
              ],
              "description": "LingoLab uses advanced AI to translate and localize your videos into 80+ languages, preserving the original voice and syncing lips for a native look.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@lingolab.ca",
                "contactType": "customer support",
                "url": "https://lingolab.ca/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-full sm:max-w-md">
        {!isForgotPassword && !isForgotUsername ? (
          <>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
              Sign In
            </h2>
            <form onSubmit={handleLogin}>
              <div className="mb-3 sm:mb-4">
                <label
                  htmlFor="username"
                  className="block text-gray-700 font-semibold mb-1 sm:mb-2"
                >
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                  required
                  autoFocus
                  autoComplete="username"
                  autoCapitalize="none"
                />
              </div>
              <div className="mb-4 sm:mb-6">
                <label
                  htmlFor="password"
                  className="block text-gray-700 font-semibold mb-1 sm:mb-2"
                >
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                  required
                  autoComplete="current-password"
                />
                <div className="mt-2 text-sm sm:text-base text-blue-500 flex justify-between">
                  <button
                    type="button"
                    onClick={() => setIsForgotUsername(true)}
                    className="hover:underline font-semibold p-1"
                  >
                    Forgot Username?
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsForgotPassword(true)}
                    className="hover:underline font-semibold p-1"
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>
              {error && (
                <p
                  className="text-red-500 text-sm sm:text-base mb-3 sm:mb-4 break-words"
                  aria-live="polite"
                >
                  {error}
                </p>
              )}
              <button
                type="submit"
                className="w-full bg-blue-500 text-white p-3 rounded-lg font-semibold hover:bg-blue-600 transition disabled:opacity-50 text-base"
                disabled={isLoading}
              >
                {isLoading ? "Signing In..." : "Sign In"}
              </button>
            </form>
            <div className="mt-3 sm:mt-4 text-center text-gray-600 text-sm sm:text-base">
              Not registered?{" "}
              <Link
                to="/register"
                className="text-blue-500 hover:underline font-semibold"
              >
                Register here
              </Link>
            </div>
          </>
        ) : isForgotPassword ? (
          <>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
              Reset Password
            </h2>
            <form onSubmit={handleForgotPassword}>
              <div className="mb-3 sm:mb-4">
                <label
                  htmlFor="email"
                  className="block text-gray-700 font-semibold mb-1 sm:mb-2"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={emailForReset}
                  onChange={(e) => setEmailForReset(e.target.value)}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>
              {resetMessage && (
                <p
                  className="text-green-500 text-sm sm:text-base mb-3 sm:mb-4 break-words"
                  aria-live="polite"
                >
                  {resetMessage}
                </p>
              )}
              {error && (
                <p
                  className="text-red-500 text-sm sm:text-base mb-3 sm:mb-4 break-words"
                  aria-live="polite"
                >
                  {error}
                </p>
              )}
              {isRegisterPrompt && (
                <p className="text-gray-600 text-sm sm:text-base mb-3 sm:mb-4">
                  Email not found.{" "}
                  <Link
                    to="/register"
                    className="text-blue-500 hover:underline font-semibold"
                  >
                    Register here
                  </Link>
                </p>
              )}
              <button
                type="submit"
                className="w-full bg-blue-500 text-white p-3 rounded-lg font-semibold hover:bg-blue-600 transition disabled:opacity-50 text-base"
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Reset Email"}
              </button>
            </form>
            <div className="mt-3 sm:mt-4 text-center">
              <button
                onClick={() => setIsForgotPassword(false)}
                className="text-blue-500 text-sm sm:text-base font-semibold hover:underline p-2"
              >
                Back to Sign In
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
              Retrieve Username
            </h2>
            <form onSubmit={handleForgotUsername}>
              <div className="mb-3 sm:mb-4">
                <label
                  htmlFor="email"
                  className="block text-gray-700 font-semibold mb-1 sm:mb-2"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={emailForReset}
                  onChange={(e) => setEmailForReset(e.target.value)}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>
              {resetMessage && (
                <p
                  className="text-green-500 text-sm sm:text-base mb-3 sm:mb-4 break-words"
                  aria-live="polite"
                >
                  {resetMessage}
                </p>
              )}
              {error && (
                <p
                  className="text-red-500 text-sm sm:text-base mb-3 sm:mb-4 break-words"
                  aria-live="polite"
                >
                  {error}
                </p>
              )}
              {isRegisterPrompt && (
                <p className="text-gray-600 text-sm sm:text-base mb-3 sm:mb-4">
                  Email not found.{" "}
                  <Link
                    to="/register"
                    className="text-blue-500 hover:underline font-semibold"
                  >
                    Register here
                  </Link>
                </p>
              )}
              <button
                type="submit"
                className="w-full bg-blue-500 text-white p-3 rounded-lg font-semibold hover:bg-blue-600 transition disabled:opacity-50 text-base"
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Username Email"}
              </button>
            </form>
            <div className="mt-3 sm:mt-4 text-center">
              <button
                onClick={() => setIsForgotUsername(false)}
                className="text-blue-500 text-sm sm:text-base font-semibold hover:underline p-2"
              >
                Back to Sign In
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SignInPage;
